/**
 * Billing & Analytics.
 *
 * The page is arranged as one question with five places to look for the
 * evidence behind it:
 *
 *   Pinned above everything — the plan: what this cycle will cost, how much of
 *   the allowance is gone and where the run rate lands. That never moves,
 *   because it is the reason anyone opens this page.
 *
 *   One period control governs the whole page, so a period chosen on Overview
 *   is still the period on Templates. Then:
 *
 *     Overview    unit economics, findings, what the money went on
 *     Plan & budget  the plan as a pool, split into template budgets — and
 *                 what happens when the budgets add up to more than the plan
 *     Usage       volume over time, request status mix, processing health
 *     Templates   which configured work is consuming the allowance, and who
 *                 raised each template's requests (drawer)
 *     People      who is consuming it, and on which templates (drawer)
 *     Costs       the cost explorer (spend by driver, template, person and
 *                 channel), then the Azure consumption estimate and the
 *                 platform's fixed cost
 *     Reports     take it away as CSV — previewed first, per template too
 *
 * Tabs beyond Overview fetch their own data on first open and then follow the
 * period control; the ones borrowing from /dashboard fail on their own without
 * taking the billing figures with them.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle, BarChart3, Compass, FileSpreadsheet, Layers, LayoutGrid, PiggyBank, RefreshCw, Users, Wallet,
} from 'lucide-react'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { useOpCo } from '../context/OpCoContext'
import { billingApi } from '../services/api'
import { useTheme } from '../context/ThemeContext'
import { getChartTheme } from '../utils/chartTheme'
import {
  billingMonthTooltip, buildInsights, formatCycleLabel, formatDaysLeft, formatPeriodLabel, resolvePlan,
  getStoredDisplayCurrency, storeDisplayCurrency,
} from '../utils/billing'
import {
  useBillingData, PERIODS, DEFAULT_PERIOD, periodLabel, emailRangeIsPeriod, isAllTimeLabel,
  formatCustomLabel, isCustomRangeReady, periodKey, parsePeriodKey, labelForKey, historyMonthKeys,
} from '../components/billing/useBillingData'
import { previousCycleOf, buildTileMetrics } from '../utils/billingTiles'
import { Card, EmptyState, Segmented, Tabs } from '../components/billing/BillingPrimitives'
import PlanSummary from '../components/billing/PlanSummary'
import PlanComparison from '../components/billing/PlanComparison'
import OpCoBillSplit from '../components/billing/OpCoBillSplit'
import MetricTiles, { METRICS } from '../components/billing/MetricTiles'
import UsageTrend from '../components/billing/UsageTrend'
import OverviewPanel from '../components/billing/OverviewPanel'
import WorkloadPanel from '../components/billing/WorkloadPanel'
import TemplatesPanel from '../components/billing/TemplatesPanel'
import PeoplePanel from '../components/billing/PeoplePanel'
import ConsumptionPanel from '../components/billing/ConsumptionPanel'
import CostExplorer from '../components/billing/CostExplorer'
import { useCostExplorer } from '../components/billing/useCostExplorer'
import InfrastructurePanel from '../components/billing/InfrastructurePanel'
import ReportsPanel from '../components/billing/ReportsPanel'
import PlanBudgetPanel from '../components/billing/PlanBudgetPanel'
import { usePlanBudgetData } from '../components/billing/usePlanBudgetData'
import IssuersCard from '../components/billing/IssuersCard'
import DetailDrawer from '../components/billing/DetailDrawer'
import PersonDrawer from '../components/billing/PersonDrawer'
import { useRequestBreakdown } from '../components/billing/useRequestBreakdown'
import { breakdownQuery } from '../utils/requestBreakdown'
import BillingTour, { hasSeenTour } from '../components/billing/BillingTour'

const TABS = [
  { value: 'overview', label: 'Overview', icon: LayoutGrid },
  { value: 'budget', label: 'Plan & budget', icon: PiggyBank },
  { value: 'usage', label: 'Usage', icon: BarChart3 },
  { value: 'templates', label: 'Templates', icon: Layers },
  { value: 'people', label: 'People', icon: Users },
  { value: 'costs', label: 'Costs', icon: Wallet },
  { value: 'reports', label: 'Reports', icon: FileSpreadsheet },
]

const ANALYSIS_PERIOD_OPTIONS = [...PERIODS, { value: 'custom', label: 'Custom' }]

/** Tabs that need data beyond the billing endpoints. */
const NEEDS_TREND = new Set(['overview', 'templates', 'reports'])
const NEEDS_WORKLOAD = new Set(['usage'])

export default function Billing() {
  const { isDark } = useTheme()
  const chart = getChartTheme(isDark)
  const toast = useToast()
  const { user, organizationName } = useAuth()
  const orgKey = user?.organization_id || organizationName || 'default'

  const [range, setRange] = useState(DEFAULT_PERIOD)
  // { from, to } (YYYY-MM-DD), only meaningful while range === 'custom'.
  // Kept even after switching away so the date inputs don't clear themselves
  // if the admin flips back to Custom.
  const [customRange, setCustomRange] = useState({ from: '', to: '' })
  const [tab, setTab] = useState('overview')
  const [metricKey, setMetricKey] = useState('pages_processed')
  const [compareOpen, setCompareOpen] = useState(false)
  const [drawerRow, setDrawerRow] = useState(null)
  const [personTarget, setPersonTarget] = useState(null)
  // Bumped by Refresh (and a drill-down's retry) so the request grid is read again.
  const [breakdownNonce, setBreakdownNonce] = useState(0)
  const closeTemplateDrawer = useCallback(() => setDrawerRow(null), [])
  const closePersonDrawer = useCallback(() => setPersonTarget(null), [])
  const retryBreakdown = useCallback(() => setBreakdownNonce(n => n + 1), [])
  const [tourOpen, setTourOpen] = useState(false)
  const [displayCurrency, setDisplayCurrency] = useState(getStoredDisplayCurrency)

  const onCurrencyChange = value => {
    setDisplayCurrency(value)
    storeDisplayCurrency(value)
  }

  // Only handed to the data hook once both ends are picked — a half-picked
  // custom range must not fire requests against a meaningless window.
  const activeCustomRange = range === 'custom' && isCustomRangeReady(customRange) ? customRange : null
  const data = useBillingData(range, activeCustomRange)
  const {
    summary, usage, history, loading, refreshing, error, usageLoading, trend, workload, loadedFor, summaryError, usageError,
  } = data
  // A failed reload keeps the earlier figures on screen (labelled with their
  // own period), so it must say so: otherwise they read as the new period's.
  const reloadFailed = Boolean(summaryError || usageError)

  // Every period label on the page comes from the period the figures beside it
  // were actually loaded for. A reload that fails leaves the previous period's
  // numbers on screen, and a label read off the control would then put the new
  // period's name on them.
  const currentKey = periodKey(range, activeCustomRange)
  const fallbackLabel = range === 'custom' ? formatCustomLabel(activeCustomRange) : periodLabel(range)
  const labelFor = key => labelForKey(key) || fallbackLabel

  // The explorer reads per-job history, the heaviest query on the page, so it
  // waits for the Costs tab to be opened once and then follows the period.
  const [explorerWanted, setExplorerWanted] = useState(false)
  useEffect(() => { if (tab === 'costs') setExplorerWanted(true) }, [tab])
  const explorer = useCostExplorer(range, activeCustomRange, explorerWanted)

  // Budgets are monthly, so their spend is always the current cycle — read
  // once the tab is first opened, independent of the period control.
  const [budgetWanted, setBudgetWanted] = useState(false)
  useEffect(() => { if (tab === 'budget') setBudgetWanted(true) }, [tab])
  const plan = useMemo(() => resolvePlan(summary), [summary])

  // The bill split by OpCo: every OpCo for an OpCo admin in the All-OpCos
  // view (its own request), the manager's own OpCo from the summary otherwise.
  const { enabled: opcoOn, allOpcos, isOpcoAdmin } = useOpCo()
  const [opcoBill, setOpcoBill] = useState(null)
  useEffect(() => {
    if (!(opcoOn && allOpcos && isOpcoAdmin) || !summary) { setOpcoBill(null); return }
    let cancelled = false
    billingApi.getByOpco()
      .then(res => { if (!cancelled) setOpcoBill(res.data?.billing || null) })
      .catch(() => { if (!cancelled) setOpcoBill(null) })
    return () => { cancelled = true }
  }, [opcoOn, allOpcos, isOpcoAdmin, summary])

  // Past billing months of that split (a closed month is frozen server-side). Offered to
  // Super Admins only.
  const splitVisible = opcoOn && isOpcoAdmin
  const [opcoCycles, setOpcoCycles] = useState([])
  const [pastStart, setPastStart] = useState(null)       // null = the open month
  const [pastBill, setPastBill] = useState(null)
  const [pastLoading, setPastLoading] = useState(false)
  const [pastError, setPastError] = useState(null)
  useEffect(() => {
    if (!splitVisible || !summary) { setOpcoCycles([]); setPastStart(null); return }
    let cancelled = false
    billingApi.getOpcoCycles()
      .then(res => { if (!cancelled) setOpcoCycles(res.data?.cycles || []) })
      .catch(() => { if (!cancelled) setOpcoCycles([]) })
    return () => { cancelled = true }
  }, [splitVisible, summary])
  useEffect(() => {
    if (!pastStart) { setPastBill(null); setPastError(null); return }
    let cancelled = false
    setPastLoading(true); setPastError(null)
    billingApi.getOpcoCycle(pastStart)
      .then(res => { if (!cancelled) setPastBill(res.data?.billing || null) })
      .catch(err => {
        if (cancelled) return
        setPastBill(null)
        setPastError(err?.response?.data?.error || 'This billing month is not available right now.')
      })
      .finally(() => { if (!cancelled) setPastLoading(false) })
    return () => { cancelled = true }
  }, [pastStart])
  const budgetData = usePlanBudgetData(plan, summary?.estimated_costs?.pricing, budgetWanted)

  const refreshAll = () => { data.refresh(); explorer.reload(); budgetData.reload(); setBreakdownNonce(n => n + 1) }

  // Each tab pulls what it needs the first time it is opened, and the hook
  // keeps it in step with the period from then on.
  const { ensureTrend, ensureWorkload } = data
  useEffect(() => {
    if (NEEDS_TREND.has(tab)) ensureTrend()
    if (NEEDS_WORKLOAD.has(tab)) ensureWorkload()
  }, [tab, ensureTrend, ensureWorkload])

  // The tour anchors to rendered elements, so it waits for the first load
  // rather than opening over a skeleton.
  useEffect(() => {
    if (!loading && summary && !hasSeenTour()) {
      const id = setTimeout(() => setTourOpen(true), 700)
      return () => clearTimeout(id)
    }
  }, [loading, summary])

  /**
   * The previous billing cycle cut at the same point of the cycle, exactly
   * (summary.previous_cycle), shared by the tiles and the insights below so a
   * cycle-to-date figure is never measured against a complete prior month
   * (which would read as a fall in usage on the 3rd of every month), nor against
   * a calendar month when the cycle runs from a Marketplace purchase date.
   * Unavailable (the first cycle after a purchase) means no comparison at all.
   */
  const previousCycle = useMemo(() => previousCycleOf(summary), [summary])

  // The request grid behind both drill-downs, read when one opens, for the
  // period the table it was opened from was loaded for (a trend cut short at
  // 800 days: the window it really covers), so the drawer counts its rows.
  const breakdownFor = useMemo(() => {
    if (drawerRow) return breakdownQuery(parsePeriodKey(loadedFor.trend), trend?.data)
    if (personTarget) return breakdownQuery(parsePeriodKey(loadedFor.summary))
    return null
  }, [drawerRow, personTarget, loadedFor.trend, loadedFor.summary, trend?.data])
  const breakdown = useRequestBreakdown(breakdownFor, breakdownNonce)

  const metrics = useMemo(
    () => buildTileMetrics({ summary, history, monthKeys: historyMonthKeys(), keys: METRICS.map(m => m.key) }),
    [summary, history],
  )

  const trendData = useMemo(
    () => (usage?.monthly_breakdown || []).map(p => ({ ...p, label: formatPeriodLabel(p.month) })),
    [usage],
  )

  const insights = useMemo(() => {
    if (!summary) return []
    return buildInsights({
      plan,
      pages: summary.current_month?.pages_processed || 0,
      prevPages: previousCycle ? previousCycle.pages_processed || 0 : null,
      people: summary.user_activity?.current_month || [],
      // Everyone's requests this cycle, not just the ten listed.
      totalRequests: summary.user_activity_totals?.current_month?.requests ?? null,
      tokens: summary.current_month?.ai_tokens_used || 0,
      displayCurrency,
    })
  }, [summary, plan, previousCycle, displayCurrency])

  const openTab = useCallback(value => {
    setTab(value)
    document.getElementById('billing-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const onInsightAction = key => {
    if (key === 'compare') setCompareOpen(true)
    if (key === 'people') openTab('people')
  }

  if (loading) return <BillingSkeleton />

  if (error && !summary) {
    return (
      <div className="p-6">
        <Card className="p-6">
          <EmptyState
            icon={AlertCircle}
            title="Could not load billing data"
            body="The usage and cost figures could not be fetched. This is usually temporary."
          />
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => data.retry()}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg
                         border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200
                         hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        </Card>
      </div>
    )
  }

  const consumption = summary?.estimated_costs?.current_month?.total_estimated_usd || 0
  // A template list that failed to load for this period is not shown under it.
  const templateSeries = trend?.error ? [] : trend?.data?.series || []

  const summaryLabel = labelFor(loadedFor.summary)
  const usageLabel = labelFor(loadedFor.usage)
  // /dashboard/trend is capped (800 days) and says when it cut the window, so
  // "All time" is only claimed when nothing was cut; otherwise the window it
  // really covers is named ("800 days", read as "in the last 800 days").
  const trendLabel = trend?.data?.truncated && isAllTimeLabel(labelFor(loadedFor.trend))
    ? `${trend.data.dates?.length || 0} days`
    : labelFor(loadedFor.trend)
  const workloadLabel = labelFor(loadedFor.workload)
  const explorerLabel = labelFor(explorer.loadedFor)
  // Reports carry the period selected, and each one is only offered when its
  // data was loaded for it. A half-picked custom range keeps the last period.
  const reportKey = range === 'custom' && !activeCustomRange ? (loadedFor.summary || currentKey) : currentKey
  const reportPeriod = parsePeriodKey(reportKey)

  return (
    <div className="p-6 space-y-5 animate-page-in">
      {/* ---- header ---- */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-header-in">
        <div className="flex items-center gap-3.5">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl
                          bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <Wallet className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                Billing &amp; Analytics
              </h1>
              <span className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold
                               text-navy-700 ring-1 ring-inset ring-navy-200
                               dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                {plan?.name}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Billing month{' '}
              <span title={billingMonthTooltip(plan?.cycle)}>{formatCycleLabel(plan?.cycle)}</span>
              {' '}· {formatDaysLeft(plan?.cycle?.days_remaining)} · usage and costs for your organisation.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Plan prices stay in the plan's currency; only the Azure estimates convert. */}
          <span title="Currency for the AI processing estimates. Plan prices always stay in pounds; converted figures are marked ≈.">
            <Segmented
              options={[{ value: 'GBP', label: 'GBP (£)' }, { value: 'USD', label: 'USD ($)' }]}
              value={displayCurrency}
              onChange={onCurrencyChange}
              ariaLabel="Currency for AI processing estimates"
            />
          </span>
          <button
            type="button"
            onClick={() => setTourOpen(true)}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-gray-200 dark:border-gray-700
                       bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200
                       hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            <Compass className="h-4 w-4" />
            Take the tour
          </button>
          <button
            type="button"
            onClick={refreshAll}
            disabled={refreshing || explorer.loading || budgetData.spendLoading}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-gray-200 dark:border-gray-700
                       bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200
                       hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing || explorer.loading || budgetData.spendLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ---- pinned: what this cycle costs ---- */}
      <PlanSummary plan={plan} onCompare={() => setCompareOpen(true)} displayCurrency={displayCurrency} />
      <OpCoBillSplit
        bill={pastStart ? pastBill : (opcoBill || summary?.opco_billing)}
        ownOnly={!opcoBill}
        displayCurrency={displayCurrency}
        cycles={opcoCycles}
        selectedStart={pastStart}
        onSelectCycle={setPastStart}
        loading={Boolean(pastStart) && pastLoading}
        error={pastStart ? pastError : null}
      />

      {/* ---- one period control for the whole page ---- */}
      <div id="billing-tabs" data-tour="period" className="sticky top-0 z-20 -mx-6 px-6 py-2.5
                                        bg-gray-50/90 dark:bg-gray-950/90
                                        backdrop-blur border-y border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">
            Analysis period
          </span>
          {/* Seven options outgrow a phone; scroll them rather than the page. */}
          <div className="max-w-full overflow-x-auto no-scrollbar">
            <Segmented options={ANALYSIS_PERIOD_OPTIONS} value={range} onChange={setRange} ariaLabel="Analysis period" />
          </div>
          {range === 'custom' && (
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                aria-label="Custom period start"
                value={customRange.from}
                max={customRange.to || undefined}
                onChange={e => setCustomRange(r => ({ ...r, from: e.target.value }))}
                className="h-7 px-2 text-xs rounded-md border border-gray-200 dark:border-gray-700
                           bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
              />
              <span className="text-xs text-gray-400 dark:text-gray-500">–</span>
              <input
                type="date"
                aria-label="Custom period end"
                value={customRange.to}
                min={customRange.from || undefined}
                max={new Date().toISOString().slice(0, 10)}
                onChange={e => setCustomRange(r => ({ ...r, to: e.target.value }))}
                className="h-7 px-2 text-xs rounded-md border border-gray-200 dark:border-gray-700
                           bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200
                           focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
              />
            </div>
          )}
          <span className="text-[11px] text-gray-400 dark:text-gray-500 hidden sm:inline">
            {range === 'custom' && !activeCustomRange
              ? 'Pick a start and end date.'
              : 'Applies to every tab except these, which have their own window: the plan panel, the Usage tiles, unit economics, findings, template allocated cost and the Budget tab show the current billing month; processing health and top issuers show all time.'}
          </span>
        </div>
      </div>

      {reloadFailed && (
        <div
          role="alert"
          className="flex items-start gap-2.5 rounded-lg border border-amber-200 dark:border-amber-800/60
                     bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-[13px] text-amber-800 dark:text-amber-300"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p className="flex-1">
            Couldn&apos;t refresh for the selected period; showing earlier figures. Each panel is labelled with
            the period its figures are for.
          </p>
          <button
            type="button"
            onClick={refreshAll}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold underline underline-offset-2
                       disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Try again
          </button>
        </div>
      )}

      {/* ---- the evidence ---- */}
      <Card className="overflow-hidden animate-card-in" style={{ animationDelay: '260ms' }}>
        <div data-tour="tabs">
          <Tabs tabs={TABS} value={tab} onChange={setTab} />
        </div>

        <div key={tab} className="animate-panel-in">
          {tab === 'overview' && (
            <div className="p-5">
              <OverviewPanel
                summary={summary}
                plan={plan}
                chart={chart}
                insights={insights}
                onInsightAction={onInsightAction}
                templates={templateSeries}
                templatesLoading={trend.loading}
                onOpenTab={openTab}
                displayCurrency={displayCurrency}
                periodLabel={summaryLabel}
              />
            </div>
          )}

          {tab === 'budget' && (
            <PlanBudgetPanel
              plan={plan}
              data={budgetData}
              orgKey={orgKey}
              chart={chart}
              displayCurrency={displayCurrency}
              onCompare={() => setCompareOpen(true)}
              onDone={msg => toast.success(msg)}
              onWarn={(title, msg) => toast.warning(title, msg)}
            />
          )}

          {tab === 'usage' && (
            <div className="p-5 space-y-5">
              <MetricTiles metrics={metrics} selected={metricKey} onSelect={setMetricKey} chart={chart} cycle={plan?.cycle} />
              <UsageTrend
                data={trendData}
                metricKey={metricKey}
                periodLabel={usageLabel}
                loading={usageLoading}
                chart={chart}
                allowance={plan?.usage?.pages_included || 0}
                grouping={usage?.grouping}
              />
              <WorkloadPanel
                workload={workload}
                chart={chart}
                periodLabel={workloadLabel}
                emailsArePeriodScoped={emailRangeIsPeriod(parsePeriodKey(loadedFor.workload).range || range)}
              />
            </div>
          )}

          {tab === 'templates' && (
            <div>
              <TemplatesPanel
                trend={trend}
                consumption={consumption}
                chart={chart}
                periodLabel={trendLabel}
                onSelect={setDrawerRow}
                displayCurrency={displayCurrency}
              />
              <div className="px-5 pb-5">
                <IssuersCard issuers={trend?.issuers} />
              </div>
            </div>
          )}

          {tab === 'people' && (
            <PeoplePanel
              activity={summary?.user_activity}
              groupActivity={summary?.group_activity}
              totals={summary?.user_activity_totals?.period}
              periodLabel={summaryLabel}
              onSelectPerson={row => setPersonTarget({ type: 'person', row })}
              onSelectOthers={others => setPersonTarget({ type: 'others', ...others })}
            />
          )}

          {tab === 'costs' && (
            <div>
              <div className="divide-y divide-gray-200 dark:divide-gray-800">
                <CostExplorer
                  explorer={explorer}
                  summary={summary}
                  chart={chart}
                  displayCurrency={displayCurrency}
                  periodLabel={explorerLabel}
                  summaryKey={loadedFor.summary}
                  onRetry={() => explorer.reload()}
                />
                <ConsumptionPanel
                  costs={summary?.estimated_costs}
                  chart={chart}
                  displayCurrency={displayCurrency}
                  periodLabel={summaryLabel}
                />
                <InfrastructurePanel infra={summary?.infrastructure_costs} displayCurrency={displayCurrency} />
              </div>
            </div>
          )}

          {tab === 'reports' && (
            <ReportsPanel
              summary={summary}
              plan={plan}
              usage={usage}
              trend={trend}
              range={reportPeriod.range}
              custom={reportPeriod.custom}
              reportKey={reportKey}
              loadedFor={loadedFor}
              loadingNow={{ summary: data.summaryLoading, usage: usageLoading }}
              organisation={organizationName}
              periodLabel={labelFor(reportKey)}
              onDone={msg => toast.success(msg)}
              onError={msg => toast.error(msg)}
            />
          )}
        </div>
      </Card>

      <BillingTour open={tourOpen} onClose={() => setTourOpen(false)} onRequestTab={setTab} />
      <PlanComparison
        plan={plan}
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        displayCurrency={displayCurrency}
      />
      <DetailDrawer
        row={drawerRow}
        dates={trend?.data?.dates}
        chart={chart}
        periodLabel={trendLabel}
        onClose={closeTemplateDrawer}
        displayCurrency={displayCurrency}
        breakdown={breakdown}
        onRetryBreakdown={retryBreakdown}
      />
      <PersonDrawer
        target={personTarget}
        breakdown={breakdown}
        periodLabel={summaryLabel}
        onClose={closePersonDrawer}
        onRetryBreakdown={retryBreakdown}
      />
    </div>
  )
}

function BillingSkeleton() {
  return (
    <div className="p-6 space-y-5">
      <div className="space-y-2">
        <div className="skeleton h-7 w-56" />
        <div className="skeleton h-4 w-80" />
      </div>
      <div className="skeleton h-52 rounded-xl" />
      <div className="skeleton h-10 rounded-lg w-full max-w-md" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map(i => <div key={i} className="skeleton h-32 rounded-xl" />)}
      </div>
      <div className="skeleton h-72 rounded-xl" />
      <span className="sr-only" role="status">Loading billing data</span>
    </div>
  )
}
